import { useQuery } from "@tanstack/react-query";
import { isUiMockMode } from "@/config/features";
import { isExportActive } from "@/domain/export/exportStatusUi";
import { MOCK_INVOICES } from "@/fixtures/invoices";
import { MOCK_EXPORT_JOBS } from "@/fixtures/exportJobs";
import { MOCK_PAY_LINKS } from "@/fixtures/payLinks";
import { MOCK_CREDIT_NOTES } from "@/fixtures/creditNotes";
import { fetchDashboardStats } from "@/features/dashboard/api/stats";
import { exportKeys } from "@/lib/query/keys";

export interface DashboardDocStats {
  allDocs: number;
  pdfs: number;
  receipts: number;
  payLinks: number;
  creditNotes: number;
  invoiceCount?: number;
  sampleLimited: boolean;
}

export interface DashboardTiles {
  totalExports: number;
  pdfsExported: number;
  inProgress: number;
}

function mockDashboardDocStats(): DashboardDocStats {
  const pdfs = MOCK_INVOICES.filter((r) => r.exportable).length;
  const receipts = MOCK_INVOICES.filter((r) => r.status === "paid").length;
  return {
    allDocs: pdfs + receipts + MOCK_PAY_LINKS.length + MOCK_CREDIT_NOTES.length,
    pdfs,
    receipts,
    payLinks: MOCK_PAY_LINKS.length,
    creditNotes: MOCK_CREDIT_NOTES.length,
    invoiceCount: MOCK_INVOICES.filter((r) => r.exportable !== false).length,
    sampleLimited: false,
  };
}

function mockDashboardTiles(): DashboardTiles {
  return {
    totalExports: MOCK_EXPORT_JOBS.length,
    pdfsExported: MOCK_EXPORT_JOBS.reduce(
      (s, j) => s + j.invoice_success_count,
      0,
    ),
    inProgress: MOCK_EXPORT_JOBS.filter((j) => isExportActive(j.status)).length,
  };
}

export function useDashboardStats() {
  const mock = isUiMockMode();

  const statsQuery = useQuery({
    queryKey: [...exportKeys.all, "dashboard-stats"],
    queryFn: () => fetchDashboardStats(),
    enabled: !mock,
  });

  if (mock) {
    return {
      docStats: mockDashboardDocStats(),
      tiles: mockDashboardTiles(),
      sampleLimited: false,
      isLoading: false,
      error: null,
    };
  }

  const s = statsQuery.data;
  const docStats: DashboardDocStats = {
    allDocs: s?.all_docs ?? 0,
    pdfs: s?.pdfs ?? 0,
    receipts: s?.receipts ?? 0,
    payLinks: s?.pay_links ?? 0,
    creditNotes: s?.credit_notes ?? 0,
    invoiceCount: s?.invoice_count ?? 0,
    sampleLimited: s?.sample_limited ?? false,
  };

  const tiles: DashboardTiles = {
    totalExports: s?.total_exports ?? 0,
    pdfsExported: s?.pdfs_exported ?? 0,
    inProgress: s?.exports_in_progress ?? 0,
  };

  return {
    docStats,
    tiles,
    sampleLimited: docStats.sampleLimited,
    isLoading: statsQuery.isLoading,
    error: statsQuery.error,
  };
}
